/* eslint-disable react-portal/no-react-router-use-navigate */
import { useCallback } from 'react';
import { t, Trans } from '@lingui/macro';
import { Box, Typography } from '@material-ui/core';
import { CheckCircleOutline, NavigateBefore } from '@material-ui/icons';
import { FormProvider, useForm } from 'react-hook-form';
import { i18n } from '@lingui/core';
import { useNavigate } from 'react-router-dom';
import { trim } from 'lodash';

import NgpTextField from '../form/NgpTextField';
import { RowFlexLayout } from '../layout';
import Link from '../links/Link';
import PasswordRequirements from '../pages-admin/users/PasswordRequirements';
import NgpPasswordField from '../form/NgpPasswordField';
import { CONFIRM_PASSWORD_FIELD, PASSWORD_FIELD } from '../pages-admin/users/UserConstants';
import { useValidationErrors, USE_FORM_DEFAULT_OPTIONS } from '../../utilities/FormUtilities';
import { useRequestState } from '../../utilities/Hooks';
import { useGlobalDataService } from '../../services/ApiDataService';
import { rethrowError } from '../../utilities/RequestUtilities';
import AuthButton from './AuthButton';
import AuthProgressButton from './AuthProgressButton';

const DEFAULT_ERROR_KEY = 'default';

const ERROR_MESSAGES = {
    invalid_access_token: t`The password reset code provided is not valid. Only the most recently provided code will be valid. If you've received more then one email, please be sure to use the most recent password reset code`,
    [DEFAULT_ERROR_KEY]: t`An unexpected error occurred trying to reset your password`,
};

export default function PasswordResetWithCode() {
    const formMethods = useForm(USE_FORM_DEFAULT_OPTIONS);

    let errorMessage;
    const { success, reset, isLoading, error } = useResetHandler();
    const password = formMethods.watch(PASSWORD_FIELD);

    useValidationErrors(error?.responseBody?.validations, formMethods.setError);

    // If we don't have specific field validations, but there was an error, display the unique key message or a generic
    // error message
    if (error && !error?.responseBody?.validations) {
        errorMessage =
            ERROR_MESSAGES[error?.responseBody?.userMessageKey] ||
            error?.responseBody?.userMessage ||
            ERROR_MESSAGES[DEFAULT_ERROR_KEY];
    }

    return (
        <>
            {!success && (
                <FormProvider {...formMethods}>
                    <form onSubmit={formMethods.handleSubmit(reset)}>
                        <Typography variant="h6">
                            <Trans>Reset Your Password</Trans>
                        </Typography>

                        <Box mt={1}>
                            <NgpTextField
                                fieldName="activateToken"
                                label={<Trans>Reset Code</Trans>}
                                required
                                inputProps={{ size: 65, maxLength: 65 }}
                            />
                        </Box>

                        <Box mt={6}>
                            <PasswordRequirements noSpacing />
                        </Box>

                        <NgpPasswordField
                            fieldName={PASSWORD_FIELD}
                            label={<Trans>Password</Trans>}
                            required
                            inputProps={{ size: 65, maxLength: 65 }}
                        />

                        <Box mt={1}>
                            <NgpPasswordField
                                fieldName={CONFIRM_PASSWORD_FIELD}
                                label={<Trans>Confirm Password</Trans>}
                                required
                                inputProps={{ size: 65, maxLength: 65 }}
                                fieldRules={{
                                    validate: {
                                        passwordsEqual: (value) =>
                                            value !== password ? i18n._(t`Passwords do not match`) : true,
                                    },
                                }}
                            />
                        </Box>

                        {errorMessage && (
                            <Box mt={3}>
                                <Typography variant="caption" color="error" component="div">
                                    {errorMessage}
                                </Typography>

                                <Box mt={2}>
                                    <AuthButton component={Link} to="/reset/request" size="small">
                                        <Trans>Request Another Code</Trans>
                                    </AuthButton>
                                </Box>
                            </Box>
                        )}

                        <RowFlexLayout mt={2}>
                            <AuthButton component={Link} to="/login" variant="text" startIcon={<NavigateBefore />}>
                                <Trans>Back to Login</Trans>
                            </AuthButton>

                            <Box flexGrow={1} />

                            <AuthProgressButton type="submit" loading={isLoading}>
                                <Trans>Reset Password</Trans>
                            </AuthProgressButton>
                        </RowFlexLayout>
                    </form>
                </FormProvider>
            )}

            {success && (
                <RowFlexLayout hCenter vCenter>
                    <CheckCircleOutline />
                    <Typography variant="subtitle2" component={Box} ml={1}>
                        <Trans>Password reset successful! Redirecting to login page.</Trans>
                    </Typography>
                </RowFlexLayout>
            )}
        </>
    );
}

function useResetHandler() {
    const dataService = useGlobalDataService();
    const navigate = useNavigate();
    const {
        data,
        isLoading,
        error,
        methods: { onInit, onError, onLoad },
    } = useRequestState();

    const reset = useCallback(
        async (formData) => {
            try {
                onInit();

                const { activateToken, password, confirmPassword } = formData;

                const queryParams = {
                    query: JSON.stringify({
                        activateToken: trim(activateToken),
                        userStatus: 'RESET',
                    }),
                };

                const putData = {
                    activateToken: trim(activateToken),
                    password,
                    confirmPassword,
                };

                await dataService.put('/ipm/public/reset', putData, queryParams, null, rethrowError);

                onLoad(true);

                setTimeout(() => navigate('/login'), 2000);
            } catch (e) {
                onError(e);
            }
        },
        [dataService, navigate, onError, onInit, onLoad]
    );

    return { reset, isLoading, error, success: data === true };
}
